/**
 * ユーザーのWebサイトに埋め込みでGemini音声チャットボットを表示させるためのスクリプト
 *
 * スプレッドシートに登録された内容だけを根拠に回答する（NotebookLM方式）。
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import embeddedRender from "./utils/embeddedRender";
import EmbeddingGeminiChat from "@/components/embedding/EmbeddingGeminiChat";
import { setBaseURL } from "@/lib/api/custom-client";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

declare global {
  // eslint-disable-next-line no-var
  var OMAKASE_GEMINI_DD: unknown; // isDuplicated
}

(() => {
  /**
   * Execution
   */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", main);
  } else {
    main();
  }

  /**
   * Functions
   */
  function main() {
    // 排他制御
    if (typeof window.OMAKASE_GEMINI_DD !== "undefined") {
      throw new Error(`OMAKASE_GEMINI のスクリプトが重複しています`);
    } else {
      window.OMAKASE_GEMINI_DD = true;
    }

    const config = getConfig();
    if (!config) return;

    // API URLの設定
    if (config.apiUrl) {
      setBaseURL(config.apiUrl);
    }

    embeddedRender(
      <QueryClientProvider client={queryClient}>
        <EmbeddingGeminiChat
          agentId={config.agentId}
          showMetrics={config.showMetrics}
          overrides={config.overrides}
        />
      </QueryClientProvider>,
      {
        parent: document.body,
        styleUrl: config.styleUrl,
      }
    );
  }

  type EmbedConfig = {
    agentId: string;
    apiUrl?: string;
    showMetrics: boolean;
    /** 埋め込み先が用意したCSS。ウィジェットのスタイルを上書きする */
    styleUrl?: string;
    /**
     * スクリプトタグで指定された表示内容。管理画面の設定より優先する。
     * 管理画面を触らずに埋め込み側だけで差し替えたい場合に使う
     */
    overrides: EmbedOverrides;
  };

  type EmbedOverrides = {
    agentName?: string;
    agentLabel?: string;
    avatarUrl?: string;
    iconUrl?: string;
    greeting?: string;
    inputPlaceholder?: string;
    idleStatus?: string;
    brandColor?: string;
  };

  /**
   * スクリプトの設定を取得
   */
  function getConfig(): EmbedConfig | null {
    const script = document.getElementById("showcan-gemini-embed-script");

    const agentId = script?.getAttribute("data-agent-id");
    if (!agentId) {
      console.warn("OMAKASE_GEMINI: data-agent-id is required");
      return null;
    }

    return {
      agentId,
      apiUrl: script?.getAttribute("data-api-url") || undefined,
      // data-show-metrics="true" でレイテンシ計測値を表示する（検証用）
      showMetrics: script?.getAttribute("data-show-metrics") === "true",
      styleUrl: script?.getAttribute("data-style-url") || undefined,
      overrides: {
        agentName: script?.getAttribute("data-agent-name") || undefined,
        agentLabel: script?.getAttribute("data-agent-label") || undefined,
        avatarUrl: script?.getAttribute("data-avatar-url") || undefined,
        iconUrl: script?.getAttribute("data-icon-url") || undefined,
        greeting: script?.getAttribute("data-greeting") || undefined,
        inputPlaceholder: script?.getAttribute("data-input-placeholder") || undefined,
        idleStatus: script?.getAttribute("data-status-text") || undefined,
        brandColor: script?.getAttribute("data-brand-color") || undefined,
      },
    };
  }
})();
